import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="contact-picker"
//
// Thin layer over the generic `searchable-select` controller (accessed via a
// Stimulus outlet) that adds an inline "+ Add new contact" form. When the
// inline contact is saved, the new option is appended to the searchable-select
// and selected automatically.
//
// The picker UI (trigger, search, options) is entirely owned by the
// searchable-select controller. This controller only owns:
//   - the mode toggle (pick mode ↔ inline create mode)
//   - the inline create form fields
//   - the JSON POST to /logbook/contacts
//
// Markup contract: see app/views/shared/_contact_picker.html.erb.
export default class extends Controller {
  static targets = [
    "pickMode", "createMode",
    "name", "organization", "email", "phone", "error"
  ]
  static values = {
    createUrl:   String,
    defaultRole: String,
    valueFormat: { type: String, default: "%id%" }
  }
  static outlets = ["searchable-select"]

  // ---- mode toggling ----

  showCreateForm() {
    if (this.hasSearchableSelectOutlet) this.searchableSelectOutlet.close()
    this.pickModeTarget.hidden = true
    this.createModeTarget.hidden = false
    this.createModeTarget.style.display = "flex"
    this.hideError()
    requestAnimationFrame(() => this.nameTarget.focus())
  }

  cancelCreate() {
    this.createModeTarget.hidden = true
    this.createModeTarget.style.display = "none"
    this.pickModeTarget.hidden = false
    this.hideError()
  }

  // ---- inline contact create ----

  async save(event) {
    event.preventDefault()
    const name = this.nameTarget.value.trim()
    if (!name) {
      this.showError("Name is required.")
      return
    }

    const tokenMeta = document.querySelector('meta[name="csrf-token"]')
    const csrf = tokenMeta ? tokenMeta.content : ""

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrf
        },
        credentials: "same-origin",
        body: JSON.stringify({
          contact: {
            name: name,
            organization: this.organizationTarget.value.trim(),
            email: this.emailTarget.value.trim(),
            phone: this.phoneTarget.value.trim(),
            role: this.defaultRoleValue
          }
        })
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        this.showError(payload.errors ? this.formatErrors(payload.errors) : "Couldn't save contact.")
        return
      }

      const contact = await response.json()
      this.injectContact(contact)
      this.resetCreateFields()
      this.cancelCreate()
    } catch (err) {
      this.showError("Network error. Try again.")
    }
  }

  injectContact(contact) {
    if (!this.hasSearchableSelectOutlet) return

    const value    = this.valueFormatValue.replace("%id%", contact.id)
    const sublabel = [contact.role, contact.organization].filter(Boolean).join(" · ")
    const search   = [contact.name, contact.email, contact.role, contact.organization].filter(Boolean).join(" ")

    this.searchableSelectOutlet.addOption({
      value:    value,
      label:    contact.name,
      sublabel: sublabel,
      search:   search,
      kind:     "contact",
      group:    "Contacts"
    })
  }

  // ---- helpers ----

  resetCreateFields() {
    this.nameTarget.value         = ""
    this.organizationTarget.value = ""
    this.emailTarget.value        = ""
    this.phoneTarget.value        = ""
    this.hideError()
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent  = message
    this.errorTarget.style.display = "block"
  }

  hideError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent  = ""
    this.errorTarget.style.display = "none"
  }

  formatErrors(errors) {
    return Object.entries(errors)
      .map(([field, msgs]) => `${field} ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`)
      .join("; ")
  }
}
